import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Undo2, Trash, Package, FolderOpen, Image as ImageIcon } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.round((now - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

const AdminTrash = () => {
  const [trash, setTrash] = useState({ projects: [], products: [] });
  const [loading, setLoading] = useState(true);
  const [pendingPurge, setPendingPurge] = useState(null); // { entity_type, id, label }
  const { isAdmin } = useCurrentAdmin();

  const token = localStorage.getItem('admin_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchTrash = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/trash`, { headers: authHeaders });
      setTrash(res.data || { projects: [], products: [], retention_days: 30 });
    } catch (err) {
      toast.error('Failed to load Trash');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrash(); }, []);

  const handleRestore = async (entityType, id, label) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/trash/${entityType}/${id}/restore`,
        {},
        { headers: authHeaders },
      );
      toast.success(`${entityType === 'project' ? 'Project' : 'Product'} "${label}" restored`);
      fetchTrash();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  const handlePurge = async () => {
    if (!pendingPurge) return;
    const { entity_type, id, label } = pendingPurge;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/trash/${entity_type}/${id}`,
        { headers: authHeaders },
      );
      toast.success(`Permanently deleted "${label}"`);
      fetchTrash();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPendingPurge(null);
    }
  };

  const renderRow = (item, entityType) => {
    const label = item.title || item.name || item.id;
    const cover = item.image || (item.images && item.images[0]) || '';
    const Icon = entityType === 'project' ? FolderOpen : Package;
    return (
      <Card key={`${entityType}-${item.id}`} data-testid={`admin-trash-row-${entityType}-${item.id}`}>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-20 h-20 flex-shrink-0 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
            {cover ? (
              <img src={resolveUrl(cover)} alt={label} className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-300" />
            )}
          </div>
          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Icon className="h-4 w-4" style={{ color: '#00CED1' }} />
              <h3 className="font-semibold truncate">{label}</h3>
              <Badge variant="outline" className="capitalize">{entityType}</Badge>
              {item.category && <Badge variant="secondary" className="capitalize">{item.category}</Badge>}
            </div>
            <p className="text-xs text-gray-500">
              Deleted {formatRelative(item.deleted_at)}
              {item.deleted_at && ` • ${new Date(item.deleted_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRestore(entityType, item.id, label)}
              style={{ borderColor: '#00CED1', color: '#00CED1' }}
              data-testid={`admin-trash-restore-${entityType}-${item.id}`}
            >
              <Undo2 className="h-4 w-4 mr-1" />
              Restore
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingPurge({ entity_type: entityType, id: item.id, label })}
                className="text-red-600 hover:text-red-700"
                data-testid={`admin-trash-purge-${entityType}-${item.id}`}
              >
                <Trash className="h-4 w-4 mr-1" />
                Delete forever
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const totalCount = trash.projects.length + trash.products.length;

  return (
    <AdminLayout>
      <div data-testid="admin-trash-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Trash2 className="h-7 w-7" style={{ color: '#00CED1' }} />
            Trash
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Soft-deleted projects and products. <strong>Restore</strong> brings them back fully; <strong>Delete forever</strong> is permanent. Items older than <strong>{trash.retention_days || 30} days</strong> are auto-purged daily.
          </p>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading Trash…</p>
        ) : totalCount === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              <Trash2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Trash is empty</p>
              <p className="text-sm mt-1">Deleted projects and products will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {trash.projects.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <FolderOpen className="h-5 w-5" style={{ color: '#00CED1' }} />
                  Projects <span className="text-sm font-normal text-gray-500">({trash.projects.length})</span>
                </h2>
                <div className="space-y-2">
                  {trash.projects.map((p) => renderRow(p, 'project'))}
                </div>
              </section>
            )}
            {trash.products.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5" style={{ color: '#00CED1' }} />
                  Products <span className="text-sm font-normal text-gray-500">({trash.products.length})</span>
                </h2>
                <div className="space-y-2">
                  {trash.products.map((p) => renderRow(p, 'product'))}
                </div>
              </section>
            )}
          </div>
        )}

        <ConfirmDialog
          open={!!pendingPurge}
          title={pendingPurge ? `Permanently delete "${pendingPurge.label}"?` : 'Permanently delete?'}
          description="This cannot be undone. The item and its database record will be removed completely. (Cloudinary images may remain.)"
          confirmLabel="Delete forever"
          destructive
          onCancel={() => setPendingPurge(null)}
          onConfirm={handlePurge}
          testId="admin-trash-confirm-purge"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTrash;
